import { Component } from '@theme/component';

const WIDGET_URL = 'https://app.thefrontrowhealth.com/api/widgets';

/**
 * Runs the FrontrowMD block's "Banner on image" and "Compact" styles.
 *
 * `data-mode="banner"` adds FrontrowMD's banner (their "sticker" widget) over the main photo of the product
 * section the block sits in. The block renders in the product details, so the frame is added to the gallery
 * from here, and added again if a section update replaces the gallery's markup. Their script sizes it and
 * toggles it between full and collapsed, finding it by the `frontrow-sticker` ID, so keep it.
 *
 * `data-mode="compact"` is our own label, rendered in place; its Learn more button opens FrontrowMD's details
 * popup, which their script sets up.
 *
 * @extends Component
 */
class FrontrowmdBanner extends Component {
  /** @type {HTMLElement | null} */
  #element = null;

  /** @type {MutationObserver | null} */
  #observer = null;

  /** Keeps the banner's size in step with the photo's width. @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    if (this.dataset.mode === 'compact') {
      // Listening on the block keeps Learn more working when a section update replaces the label's markup.
      this.addEventListener(
        'click',
        (event) => {
          const button = event.target instanceof Element ? event.target.closest('[data-frontrowmd-learn-more]') : null;
          if (button instanceof HTMLButtonElement) this.#openDetails(button);
        },
        { signal: this.#abortController.signal }
      );
      if (this.dataset.productId) this.#loadScript();
      return;
    }

    this.#place();

    this.#observer?.disconnect();
    this.#observer = new MutationObserver(() => {
      if (!this.#element?.isConnected) this.#place();
    });
    this.#observer.observe(this.#section, { childList: true, subtree: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController?.abort();
    this.#abortController = null;
    this.#observer?.disconnect();
    this.#observer = null;
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    this.#element?.remove();
    this.#element = null;
  }

  get #section() {
    return this.closest('.shopify-section') ?? document.body;
  }

  get #query() {
    return new URLSearchParams({ presentation_type: 'sticker', product_id: this.dataset.productId ?? '' }).toString();
  }

  #place() {
    const container = this.#section.querySelector('.product-media-gallery-badge-wrapper slideshow-container');
    if (!this.dataset.productId || !container) return;

    this.#element ??= this.#createFrame();

    container.append(this.#element);
    this.#loadScript();

    this.#resizeObserver?.disconnect();
    this.#resizeObserver = new ResizeObserver(() => this.#updateScale(container));
    this.#resizeObserver.observe(container);
    // The frame gets its real width once FrontrowMD's styles load.
    this.#resizeObserver.observe(this.#element);

    // FrontrowMD fits the banner once when the frame asks; size it again right after.
    window.addEventListener(
      'message',
      (event) => {
        if (event.source === this.#element?.contentWindow && event.data?.name === 'RESIZE_STICKER') {
          setTimeout(() => this.#updateScale(container));
        }
      },
      { signal: this.#abortController?.signal }
    );
  }

  /**
   * Sizes the banner to the photo: the block's Banner size on desktop, and never wider than 90% of the photo
   * (FrontrowMD's own fit). Their script fits it only once, when it loads, so a window resize would otherwise
   * leave it hanging off the photo. Runs whenever the photo or the frame changes size, and again right after
   * their one-time fit so it has the last word.
   * @param {Element} container
   */
  #updateScale(container) {
    const frame = this.#element;
    // Until FrontrowMD's styles give the frame its real width it has the browser's default 300px.
    if (!(frame instanceof HTMLIFrameElement) || !(container instanceof HTMLElement) || frame.offsetWidth <= 300) return;

    const desktop = window.matchMedia('(min-width: 750px)').matches;
    const target = desktop ? Number(this.dataset.scale) || 1 : 1;
    const fit = (container.offsetWidth * 0.9) / frame.offsetWidth;
    const zoom = Math.min(1, target, fit);

    frame.style.transform = zoom < 1 ? `scale(${zoom})` : '';
    // Phones always show it at the bottom (see the block's CSS).
    frame.style.transformOrigin = this.dataset.position === 'bottom' || !desktop ? '0 100%' : '0 0';
    frame.contentWindow?.postMessage({ name: 'STICKER_RESIZED', data: { zoom } }, '*');
  }

  #createFrame() {
    const frame = document.createElement('iframe');
    frame.id = 'frontrow-sticker';
    frame.className = 'iframe-frontrow-sticker frontrowmd-banner__frame';
    if (this.dataset.position === 'bottom') frame.classList.add('frontrowmd-banner__frame--bottom');
    frame.dataset.state = 'max';
    frame.title = this.dataset.title ?? '';
    frame.src = `${WIDGET_URL}?${this.#query}`;
    return frame;
  }

  /** @returns {HTMLScriptElement} */
  #loadScript() {
    const existing = document.querySelector('script[data-frontrowmd-banner]');
    if (existing instanceof HTMLScriptElement) return existing;

    const script = document.createElement('script');
    script.async = true;
    script.src = `${WIDGET_URL}/script?${this.#query}`;
    script.dataset.frontrowmdBanner = '';
    script.addEventListener('load', () => (script.dataset.loaded = ''), { once: true });
    document.body.append(script);
    return script;
  }

  /**
   * Opens FrontrowMD's details popup, once their script has loaded, and returns focus to the button when the
   * popup reports that it closed.
   * @param {HTMLButtonElement} button
   */
  async #openDetails(button) {
    const script = this.#loadScript();
    if (!('loaded' in script.dataset)) {
      await new Promise((resolve) => script.addEventListener('load', resolve, { once: true }));
    }

    const openFrModal = /** @type {any} */ (window).openFrModal;
    if (typeof openFrModal !== 'function') return;
    openFrModal('sticker', 'compact');

    /** @param {MessageEvent} event */
    const handleMessage = (event) => {
      const name = typeof event.data === 'string' ? event.data : event.data?.name;
      if (typeof name !== 'string' || !name.includes('CLOSE')) return;
      window.removeEventListener('message', handleMessage);
      button.focus();
    };
    window.addEventListener('message', handleMessage, { signal: this.#abortController?.signal });
  }
}

if (!customElements.get('frontrowmd-banner')) {
  customElements.define('frontrowmd-banner', FrontrowmdBanner);
}
